/**
 * Minimal W3C Trace Context support for ontology-ui.
 *
 * The UI does not run a browser OTEL SDK; it only mints a `traceparent`
 * header per outgoing request so the server-side spans (axilon-api-service,
 * proxy-server) share a UI-originated trace ID and a click can be followed
 * end-to-end in Tempo. Format: https://www.w3.org/TR/trace-context/
 */

export const TRACEPARENT_HEADER = 'traceparent'

function randomHex(bytes: number): string {
  const buf = new Uint8Array(bytes)
  crypto.getRandomValues(buf)
  return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('')
}

function nonZeroHex(bytes: number): string {
  // The spec forbids all-zero trace/span ids; regenerate on the (astronomically
  // unlikely) zero draw.
  let hex = randomHex(bytes)
  while (/^0+$/.test(hex)) {
    hex = randomHex(bytes)
  }
  return hex
}

/**
 * Mint a new sampled W3C traceparent: `00-{32 hex trace-id}-{16 hex
 * parent-id}-01`. Each call starts a new trace — callers attach it to exactly
 * one outgoing request.
 */
export function generateTraceparent(): string {
  return `00-${nonZeroHex(16)}-${nonZeroHex(8)}-01`
}

// ─── Per-navigation trace context (AXI-3875) ──────────────────────────────
//
// To make a slow screen deep-link to the backend spans it triggered, the
// requests a screen fires during its load window must share ONE trace id
// instead of each minting its own. The route telemetry tracker opens a
// navigation trace on every route change (`beginNavigationTrace`) and closes
// it once the screen's data has settled (`endNavigationTrace`, at TTFD or a
// hard cap). While a navigation trace is active, every egress point asks for
// `nextRequestTraceparent()` instead of `generateTraceparent()`, so the
// api-service / proxy / Neo4j spans all land in the same Tempo trace as the
// `ui.navigation` span the collector emits from the matching TTID/TTFD beacon.
//
// `rootSpanId` is used as the W3C parent-id for every in-window request. There
// is no real browser span behind it (the UI runs no OTEL SDK); it is a
// synthetic join point. The collector's `ui.navigation` span and the screen's
// backend spans therefore assemble as siblings under one trace id — enough to
// follow a click end-to-end. After the window closes, requests revert to
// per-request traces so background polling never grows one unbounded trace.

/** Active navigation trace: a shared trace id plus its synthetic parent span id. */
export interface NavigationTrace {
  traceId: string
  rootSpanId: string
}

let activeNavigationTrace: NavigationTrace | null = null

/**
 * Open a fresh navigation trace and make it the active one. Called once per
 * route change. Returns the new trace so the caller can stamp the matching
 * TTID/TTFD beacon with the same ids.
 */
export function beginNavigationTrace(): NavigationTrace {
  activeNavigationTrace = { traceId: nonZeroHex(16), rootSpanId: nonZeroHex(8) }
  return activeNavigationTrace
}

/** The currently-active navigation trace, or null when none is open. */
export function getActiveNavigationTrace(): NavigationTrace | null {
  return activeNavigationTrace
}

/** Close the active navigation trace (at TTFD / cap). Subsequent requests mint fresh traces. */
export function endNavigationTrace(): void {
  activeNavigationTrace = null
}

/**
 * The traceparent to attach to one outgoing request. Reuses the active
 * navigation trace's ids (so the request joins the screen's trace) when a
 * navigation is in flight; otherwise mints a fresh single-request trace —
 * identical to the previous always-fresh behaviour.
 */
export function nextRequestTraceparent(): string {
  if (activeNavigationTrace) {
    return `00-${activeNavigationTrace.traceId}-${activeNavigationTrace.rootSpanId}-01`
  }
  return generateTraceparent()
}
